import { supabase } from '@/integrations/supabase/client';

export type ActivityActionType =
  | 'negotiation_started'
  | 'negotiation_cancelled'
  | 'negotiation_completed'
  | 'listing_created'
  | 'earning_created'
  | 'earning_deleted'
  | 'arsenal_video_added'
  | 'student_created'
  | 'student_activated'
  | 'student_deactivated'
  | 'student_subscription_cancelled'
  | 'visitor_registered'
  | 'sparks_purchased'
  | 'sparks_purchase_failed'
  | 'sparks_consumed'
  | 'sparks_adjusted'
  | 'sparks_daily_granted'
  | 'sparks_bonus_granted'
  | 'student_lost_acknowledged'
  | 'negotiation_expired'
  | 'support_ticket_created'
  | 'support_ticket_closed'
  | 'pwa_installed'
  | 'penalty_notification_read'
  | 'seller_verification_submitted'
  | 'seller_verification_resubmitted'
  | 'credit_recharge_submitted'
  | 'credit_recharge_resubmitted'
  | 'plan_change_submitted'
  | 'plan_change_resubmitted';

/**
 * Fire-and-forget activity log insert.
 * Never awaited in the main flow — failures are silent.
 */
export function logActivity(
  action_type: ActivityActionType,
  actor_id: string,
  actor_name: string | null,
  target_name?: string | null,
  reference_id?: string | null,
  metadata?: Record<string, unknown> | null
): void {
  supabase
    .from('activity_logs' as any)
    .insert({
      actor_id,
      actor_name: actor_name || 'Usuário',
      action_type,
      target_name: target_name || null,
      reference_id: reference_id || null,
      metadata: metadata || null,
    })
    .then(({ error }) => {
      if (error) console.warn('[ActivityLog] Insert failed:', error.message);
    });
}

/**
 * Fire-and-forget: fetches the actor's display_name from public_profiles
 * then calls logActivity. Eliminates the repeated boilerplate across services.
 */
export function logActivityWithProfile(
  action_type: ActivityActionType,
  actor_id: string,
  target_name?: string | null,
  reference_id?: string | null,
  metadata?: Record<string, unknown> | null
): void {
  supabase
    .from('public_profiles' as any)
    .select('display_name')
    .eq('user_id', actor_id)
    .single()
    .then(({ data }) => {
      logActivity(action_type, actor_id, (data as any)?.display_name ?? 'Usuário', target_name, reference_id, metadata);
    });
}

export interface ActivityLog {
  id: string;
  created_at: string;
  actor_id: string;
  actor_name: string | null;
  action_type: ActivityActionType;
  target_name: string | null;
  reference_id: string | null;
  metadata: Record<string, unknown> | null;
}

export async function fetchActivityLogs(
  limit = 50,
  offset = 0
): Promise<ActivityLog[]> {
  const { data, error } = await supabase
    .from('activity_logs' as any)
    .select('*')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) throw error;
  return (data || []) as unknown as ActivityLog[];
}

// ── Sparks Ledger → ActivityLog conversion ──────────

const REASON_TO_ACTION: Record<string, ActivityActionType> = {
  tool_charge: 'sparks_consumed',
  purchase: 'sparks_purchased',
  admin_adjust: 'sparks_adjusted',
  visitor_daily: 'sparks_daily_granted',
  student_signup_bonus: 'sparks_bonus_granted',
};

interface LedgerRow {
  id: string;
  user_id: string;
  delta_int: number;
  reason: string;
  tool_key: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export async function fetchSparksActivity(limit = 50): Promise<ActivityLog[]> {
  // Fetch recent ledger entries
  const { data: ledger, error: ledgerErr } = await supabase
    .from('sparks_ledger' as any)
    .select('id, user_id, delta_int, reason, tool_key, metadata, created_at')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (ledgerErr) throw ledgerErr;

  const rows = (ledger || []) as unknown as LedgerRow[];

  // Get unique user IDs and fetch display names
  const userIds = [...new Set(rows.map((r) => r.user_id))];

  // Fetch profiles and failed/expired transactions in parallel
  const [profilesRes, transactionsRes] = await Promise.all([
    supabase
      .from('public_profiles' as any)
      .select('user_id, display_name')
      .in('user_id', userIds.length > 0 ? userIds : ['__none__']),
    supabase
      .from('sparks_transactions' as any)
      .select('id, user_id, sparks_amount_int, status, created_at, stripe_payment_intent_id')
      .in('status', ['expired', 'pending'])
      .order('created_at', { ascending: false })
      .limit(limit),
  ]);

  const nameMap = new Map<string, string>();
  if (profilesRes.data) {
    for (const p of profilesRes.data as any[]) {
      if (p.display_name) nameMap.set(p.user_id, p.display_name);
    }
  }

  // Build failed transaction logs
  const failedLogs: ActivityLog[] = [];
  if (transactionsRes.data) {
    // Also fetch names for failed transaction users
    const failedUserIds = [...new Set((transactionsRes.data as any[]).map((t: any) => t.user_id))];
    const missingIds = failedUserIds.filter((id) => !nameMap.has(id));
    if (missingIds.length > 0) {
      const { data: extraProfiles } = await supabase
        .from('public_profiles' as any)
        .select('user_id, display_name')
        .in('user_id', missingIds);
      if (extraProfiles) {
        for (const p of extraProfiles as any[]) {
          if (p.display_name) nameMap.set(p.user_id, p.display_name);
        }
      }
    }

    for (const t of transactionsRes.data as any[]) {
      failedLogs.push({
        id: `spark-fail-${t.id}`,
        created_at: t.created_at,
        actor_id: t.user_id,
        actor_name: nameMap.get(t.user_id) || 'Usuário',
        action_type: 'sparks_purchase_failed',
        target_name: null,
        reference_id: t.id,
        metadata: {
          delta_int: t.sparks_amount_int,
          status: t.status,
        },
      });
    }
  }

  // Deduplicate purchase ledger entries by ref_id (payment intent)
  const seenPurchaseRefs = new Set<string>();
  const dedupedRows = rows.filter((r) => {
    if (r.reason !== 'purchase') return true;
    const refId = (r.metadata as any)?.stripe_payment_intent_id || r.id;
    if (seenPurchaseRefs.has(refId)) return false;
    seenPurchaseRefs.add(refId);
    return true;
  });

  const ledgerLogs: ActivityLog[] = dedupedRows
    .filter((r) => REASON_TO_ACTION[r.reason])
    .map((r) => ({
      id: `spark-${r.id}`,
      created_at: r.created_at,
      actor_id: r.user_id,
      actor_name: nameMap.get(r.user_id) || 'Usuário',
      action_type: REASON_TO_ACTION[r.reason],
      target_name: r.tool_key || null,
      reference_id: r.id,
      metadata: {
        ...(r.metadata || {}),
        delta_int: r.delta_int,
        tool_key: r.tool_key,
      },
    }));

  return [...ledgerLogs, ...failedLogs];
}
